// CSV Parser function that handles quoted fields properly
export function parseCSV(csvText: string): { headers: string[], rows: string[][] } {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  // Parse headers
  const headers = parseCSVLine(lines[0]);

  // Parse data rows
  const rows: string[][] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line) {
      const row = parseCSVLine(line);
      // Only add rows that have the same number of columns as headers
      if (row.length >= headers.length) {
        rows.push(row);
      }
    }
  }

  return { headers, rows };
}

// Parse a single CSV line handling quoted fields
export function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote inside quoted field
        current += '"';
        i += 2; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator outside quotes
      result.push(current.trim());
      current = '';
      i++;
    } else {
      current += char;
      i++;
    }
  }

  // Add the last field
  result.push(current.trim());

  return result;
}

// Auto-detect column mapping based on header names
export function autoDetectMapping(headers: string[]): { filename: string; title: string; description: string; keywords: string } {
  const mapping = { filename: '', title: '', description: '', keywords: '' };

  // Convert headers to lowercase for case-insensitive matching
  const lowerHeaders = headers.map(h => h.toLowerCase().trim());

  // Filename detection patterns
  const filenamePatterns = ['filename', 'file', 'name', 'file_name', 'filename', 'image', 'photo', 'picture'];
  mapping.filename = findBestMatch(lowerHeaders, filenamePatterns, headers);

  // Title detection patterns
  const titlePatterns = ['title', 'headline', 'heading', 'caption', 'subject'];
  mapping.title = findBestMatch(lowerHeaders, titlePatterns, headers);

  // Description detection patterns
  const descriptionPatterns = ['description', 'desc', 'content', 'text', 'body', 'details', 'info'];
  mapping.description = findBestMatch(lowerHeaders, descriptionPatterns, headers);

  // Keywords detection patterns
  const keywordsPatterns = ['keywords', 'keyword', 'tags', 'tag', 'category', 'categories'];
  mapping.keywords = findBestMatch(lowerHeaders, keywordsPatterns, headers);

  return mapping;
}

// Find best match for a field based on patterns
export function findBestMatch(lowerHeaders: string[], patterns: string[], originalHeaders: string[]): string {
  // First, try exact matches
  for (let i = 0; i < lowerHeaders.length; i++) {
    if (patterns.includes(lowerHeaders[i])) {
      return originalHeaders[i];
    }
  }

  // Then, try partial matches (contains pattern)
  for (let i = 0; i < lowerHeaders.length; i++) {
    for (const pattern of patterns) {
      if (lowerHeaders[i].includes(pattern)) {
        return originalHeaders[i];
      }
    }
  }

  // Return empty if no match found
  return '';
}

// CSV Data Types
export interface CSVRow {
  filename: string;
  title: string;
  description: string;
  keywords: string;
  // Additional fields that can be present in the CSV
  [key: string]: string;
}

export interface CSVData {
  headers: string[];
  rows: CSVRow[];
  // Track which headers are mapped to standard fields
  mappedHeaders: { [key: string]: string };
}

